import { TZDate, TZDateMini } from '@date-fns/tz';
import { addDays, startOfWeek, eachDayOfInterval } from 'date-fns';
import { useMemo, type Dispatch } from 'react';

import { formatDate } from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { useCalendarContext } from './useCalendarContext.js';
import type { CalendarReducerAction } from '../types/shared-types.js';
import { getDays } from '../utils/helpers.js';

type Weekday = {
  date: TZDate;
  longName: string;
  shortName: string;
};

/** @internal */
export interface _Day {
  date: TZDate;
  isCurrentMonthDay: boolean;
}
export interface _SelectDayViewItems {
  weekdays: Weekday[];
  days: _Day[];
  dispatch: Dispatch<CalendarReducerAction>;
}

/** Custom hook used for the day selection view. */
export function useSelectDay(): _SelectDayViewItems {
  const { state, dispatch } = useCalendarContext();

  const weekdays = useMemo(() => {
    // Extract the user-settings timezone to use for the calculation
    const configuredTimezone = getTimezone();

    // Generate an array of dates representing each day of the week
    const today = TZDateMini.tz(configuredTimezone);
    const intervalStart = startOfWeek(today, {
      weekStartsOn: state.weekStartsOn,
    });

    // Generate an array of dates representing each day of the week
    const days = eachDayOfInterval({
      start: intervalStart,
      end: addDays(intervalStart, 6),
    });

    return days.map((day) => ({
      date: day,
      longName: formatDate(day, {
        weekday: 'long',
        timeZone: configuredTimezone,
      }),
      shortName: formatDate(day, {
        weekday: 'short',
        timeZone: configuredTimezone,
      }),
    }));
  }, [state.weekStartsOn]);

  const days = useMemo(
    () => getDays(state.activeDate, state.weekStartsOn),
    [state.activeDate, state.weekStartsOn],
  );

  return {
    weekdays,
    days,
    dispatch,
  };
}
